<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Appointment Notification Settings"
      subtitle="Configure fallback email for new appointment notifications"
      icon="fas fa-envelope-open-text"
      gradient="teal"
    />

    <BCard class="border shadow-sm">
      <BOverlay
        :show="state.loading"
        opacity="0.5"
      >
        <BAlert
          v-if="state.error"
          variant="danger"
          show
          class="mb-3"
        >
          {{ state.error }}
        </BAlert>

        <BAlert
          show
          variant="info"
          class="mb-3"
        >
          This email is used only when a slot has no <strong>Notification Email</strong>. It will only receive alerts for <strong>new appointment submissions</strong>.
        </BAlert>

        <BForm @submit.prevent="saveSettings">
          <BRow>
            <BCol
              cols="12"
              md="8"
            >
              <BFormGroup
                label="Default Notification Email"
                label-for="defaultNotificationEmail"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="defaultNotificationEmail"
                  v-model.trim="form.default_notification_email"
                  type="email"
                  placeholder="example@domain.com"
                  :disabled="state.saving"
                />
                <small class="text-muted">Leave blank to disable fallback email.</small>

                <div class="mt-2 d-flex align-items-center gap-2 flex-wrap">
                  <BButton
                    size="sm"
                    variant="outline-success"
                    :disabled="state.saving || otp.sending || otp.cooldown > 0 || !form.default_notification_email"
                    @click="sendOtp"
                  >
                    <i :class="['fas me-1', otp.sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                    {{ otp.sending ? 'Sending OTP...' : 'Send OTP' }}
                  </BButton>

                  <small
                    v-if="otp.cooldown > 0"
                    class="text-muted"
                  >
                    Resend available in {{ otp.cooldown }}s
                  </small>
                </div>

                <div
                  v-if="otp.sent && !otp.verified"
                  class="mt-2 d-flex align-items-center gap-2 flex-wrap"
                >
                  <BFormInput
                    v-model.trim="otp.code"
                    type="text"
                    maxlength="6"
                    placeholder="Enter 6-digit OTP"
                    :disabled="state.saving || otp.verifying"
                    style="max-width: 220px;"
                  />

                  <BButton
                    size="sm"
                    variant="success"
                    :disabled="state.saving || otp.verifying || otp.code.length !== 6"
                    @click="verifyOtp"
                  >
                    <i :class="['fas me-1', otp.verifying ? 'fa-spinner fa-spin' : 'fa-check']" />
                    {{ otp.verifying ? 'Verifying...' : 'Verify OTP' }}
                  </BButton>
                </div>

                <small
                  v-if="otp.success"
                  class="text-success d-block mt-2"
                >
                  <i class="fas fa-check-circle me-1" />
                  {{ otp.success }}
                </small>

                <small
                  v-if="otp.error"
                  class="text-danger d-block mt-2"
                >
                  <i class="fas fa-exclamation-circle me-1" />
                  {{ otp.error }}
                </small>

                <small
                  v-if="requiresOtpVerification"
                  class="text-warning d-block mt-2"
                >
                  <i class="fas fa-shield-alt me-1" />
                  Verify this email via OTP before saving changes.
                </small>
              </BFormGroup>
            </BCol>
          </BRow>

          <div class="d-flex justify-content-end mt-3">
            <BButton
              type="submit"
              variant="primary"
              :disabled="state.saving"
            >
              <i :class="['fas me-1', state.saving ? 'fa-spinner fa-spin' : 'fa-save']" />
              {{ state.saving ? 'Saving...' : 'Save Settings' }}
            </BButton>
          </div>
        </BForm>
      </BOverlay>
    </BCard>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import { SAdminAppointmentEmailOtp, SAdminAppointmentNotificationSetting } from '@/services'

export default {
  name: 'AppointmentNotificationSettings',

  middleware: ['auth'],

  metaInfo: () => ({ title: 'Appointment Notification Settings' }),

  data () {
    return {
      form: {
        default_notification_email: ''
      },
      originalEmail: '',
      otpTimerId: null,
      otp: {
        sending: false,
        sent: false,
        verifying: false,
        verified: false,
        code: '',
        token: '',
        verifiedEmail: '',
        cooldown: 0,
        success: '',
        error: ''
      },
      state: {
        loading: false,
        saving: false,
        error: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    normalizedEmail () {
      return this.normalizeEmail(this.form.default_notification_email)
    },

    requiresOtpVerification () {
      return this.normalizedEmail !== '' && this.normalizedEmail !== this.originalEmail
    }
  },

  watch: {
    normalizedEmail (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.clearOtpStateForEmailChange()
      }
    }
  },

  beforeDestroy () {
    this.clearOtpTimer()
  },

  mounted () {
    this.loadSettings()
  },

  methods: {
    async loadSettings () {
      this.state.loading = true
      this.state.error = ''

      try {
        const { data } = await SAdminAppointmentNotificationSetting.show()
        const payload = data?.data || {}
        this.form.default_notification_email = payload.default_notification_email || ''
        this.originalEmail = this.normalizeEmail(this.form.default_notification_email)
      } catch (error) {
        this.state.error = error?.response?.data?.message || 'Failed to load appointment notification settings.'
      } finally {
        this.state.loading = false
      }
    },

    normalizeEmail (value) {
      return (value || '').trim().toLowerCase()
    },

    clearOtpTimer () {
      if (this.otpTimerId) {
        clearInterval(this.otpTimerId)
        this.otpTimerId = null
      }
    },

    startOtpCooldown (seconds = 60) {
      this.clearOtpTimer()
      this.otp.cooldown = seconds

      this.otpTimerId = setInterval(() => {
        this.otp.cooldown = Math.max(0, this.otp.cooldown - 1)
        if (this.otp.cooldown === 0) {
          this.clearOtpTimer()
        }
      }, 1000)
    },

    clearOtpStateForEmailChange () {
      this.otp.sent = false
      this.otp.verified = false
      this.otp.code = ''
      this.otp.token = ''
      this.otp.verifiedEmail = ''
      this.otp.success = ''
      this.otp.error = ''
      this.clearOtpTimer()
      this.otp.cooldown = 0
    },

    async sendOtp () {
      this.otp.error = ''
      this.otp.success = ''

      const email = this.normalizedEmail
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!emailRegex.test(email)) {
        this.otp.error = 'Please enter a valid email address before requesting OTP.'
        return
      }

      this.otp.sending = true

      try {
        const { data } = await SAdminAppointmentEmailOtp.send(email)
        this.otp.sent = true
        this.otp.verified = false
        this.otp.code = ''
        this.otp.token = ''
        this.otp.verifiedEmail = ''
        this.otp.success = data?.message || 'OTP sent successfully.'
        this.startOtpCooldown(60)
      } catch (error) {
        this.otp.error = error?.response?.data?.message || 'Failed to send OTP.'
      } finally {
        this.otp.sending = false
      }
    },

    async verifyOtp () {
      this.otp.error = ''
      this.otp.success = ''

      const email = this.normalizedEmail
      const code = (this.otp.code || '').trim()

      if (!/^\d{6}$/.test(code)) {
        this.otp.error = 'Please enter the 6-digit OTP.'
        return
      }

      this.otp.verifying = true

      try {
        const { data } = await SAdminAppointmentEmailOtp.verify(email, code)
        this.otp.verified = true
        this.otp.token = data?.data?.verification_token || ''
        this.otp.verifiedEmail = email
        this.otp.success = data?.message || 'Email verified successfully.'
      } catch (error) {
        this.otp.verified = false
        this.otp.token = ''
        this.otp.verifiedEmail = ''
        this.otp.error = error?.response?.data?.message || 'Invalid OTP.'
      } finally {
        this.otp.verifying = false
      }
    },

    async saveSettings () {
      this.state.saving = true
      this.state.error = ''

      if (this.requiresOtpVerification) {
        if (!(this.otp.verified && this.otp.token && this.otp.verifiedEmail === this.normalizedEmail)) {
          this.state.saving = false
          this.state.error = 'Please verify the email using OTP before saving.'
          return
        }
      }

      try {
        const payload = {
          default_notification_email: this.form.default_notification_email || null,
          otp_verification_token: this.requiresOtpVerification ? this.otp.token : null
        }

        const { data } = await SAdminAppointmentNotificationSetting.update(payload)
        this.form.default_notification_email = data?.data?.default_notification_email || ''
        this.originalEmail = this.normalizeEmail(this.form.default_notification_email)
        this.clearOtpStateForEmailChange()

        await this.$swal({
          icon: 'success',
          title: 'Saved',
          text: data?.message || 'Appointment notification settings updated successfully.'
        })
      } catch (error) {
        this.state.error = error?.response?.data?.message || 'Failed to save appointment notification settings.'
      } finally {
        this.state.saving = false
      }
    }
  }
}
</script>
